import React, { memo } from 'react'
import useTabAlreadyOpen from 'hooks/useTabAlreadyOpen'
import ScheduleContent from 'components/appointments/Content'
import HomeContent from 'components/home/Content'
import PatientContent from 'components/patient/Content'
import { AnimatePresence } from 'framer-motion'
import { ROUTES } from 'Constants'
import styled from 'styled-components'
import { background } from 'styles/Colors'
import { useLocation } from 'react-router-dom'

import AccountContent from 'components/account/Content'
import WalletContent from 'components/wallet/Content'
import useAutoScale from 'hooks/useAutoScale'

import LogoutButton from 'components/logoutButton'

import OtherTabOpen from './OtherTabOpen'
import TopBar from './TopBar'
import Sidebar from './Sidebar'
import AVConsult from './AVConsult'
import Notification from './Notification'

export const Container = styled.div`
  background-color: ${background};
  height: 100vh;
  width: 100%;
  min-width: 1024px;
  overflow: hidden;
  display: flex;
`

const Footer = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 24px;
  background-color: #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9990;
  pointer-events: none;
  font-family: coresanscr55, sans-serif;
  font-size: 11px;
  color: #8a8a8a;
  letter-spacing: 0.02em;
`

const Dashboard = () => {
  const otherTabOpen = useTabAlreadyOpen()
  const location = useLocation()
  const scale = useAutoScale()

  if (otherTabOpen) {
    return (
      <Container>
        <OtherTabOpen />
      </Container>
    )
  }

  // When scale < 1 the inner content is rendered at a larger virtual size
  // then shrunk via CSS transform so everything fits in the viewport.
  const needsScale = scale < 1
  const virtualHeight = `${100 / scale}vh`
  const virtualWidth = `${100 / scale}vw`
  const innerStyle: React.CSSProperties = needsScale
    ? {
        width: virtualWidth,
        height: virtualHeight,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }
    : {}

  // The inner layout must use the virtual height, not browser 100vh,
  // so it fills the full scaled container.
  const layoutHeight = needsScale ? virtualHeight : '100vh'

  return (
    <>
      <div style={needsScale ? { width: '100vw', height: '100vh', overflow: 'hidden' } : undefined}>
        <div style={innerStyle}>
          <LogoutButton />
          <div className="overflow-hidden flex" style={{ minWidth: 1024, height: layoutHeight }}>
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden" style={{ height: layoutHeight }}>
              <TopBar />
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {/* @ts-ignore */}
                <AnimatePresence>
                  {location.pathname === ROUTES.HOME && <HomeContent />}
                  {location.pathname === ROUTES.SCHEDULE && <ScheduleContent />}
                  {location.pathname.startsWith(ROUTES.PATIENT) && <PatientContent />}
                  {location.pathname.startsWith(ROUTES.ACCOUNT) && <AccountContent />}
                  {location.pathname.startsWith(ROUTES.WALLET) && <WalletContent />}
                </AnimatePresence>
              </div>
            </div>
            <div className="border-l border-grey-100" style={{ width: '25%', minWidth: 280, maxWidth: 380, height: layoutHeight }}>
              <Sidebar />
            </div>
          </div>
        </div>
      </div>
      <AVConsult />
      <Notification />
      <Footer>© {new Date().getFullYear()} Medvice</Footer>
    </>
  )
}

Dashboard.whyDidYouRender = true

export default memo(Dashboard)
